/**
 * The stairs down the torii axis to the lane, from site.ts STAIRS_PLAN: one solid stone body (treads, risers, side
 * walls down into the ground, a 0.75 m curb each side), wooden rails on the curb that continue the terrace fence
 * (outside the torii pillars; each starts with a post on the terrace), and small stone lamps outside the left rail.
 * Code-built: the step count comes from the terrain.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { STAIRS_BODY_HALF_M, STAIRS_TOP_ALONG_M, STAIRS_WIDTH_M, STAIR_RAIL_OFFSET_M } from '../composition';
import { LANDING_M, STAIRS_PLAN, STEP_RISE_M, STEP_TREAD_M, axisPoint, surfaceY } from '../site';

export { STAIRS_BODY_HALF_M };

const POST_H = 0.82;
const RAIL_HEIGHTS: Array<[number, number, number]> = [[0.74, 0.1, 0.08], [0.4, 0.07, 0.06]]; // height, width, depth
const LAMP_EVERY = 5;
const LAMP_ACROSS = -(STAIRS_WIDTH_M / 2 + 0.5); // on the left curb, outside the rail

/** Unit vectors in world (x, z): along the axis (downhill) and across it. (X, Y, U) is right-handed. */
const [ax0, az0] = axisPoint(0);
const [ax1, az1] = axisPoint(1);
const U = new THREE.Vector3(ax1 - ax0, 0, az1 - az0).normalize();
const X = new THREE.Vector3(U.z, 0, -U.x); // X × Y = U: a left-handed basis mirrors boxes inside-out
const BASIS = new THREE.Matrix4().makeBasis(X, new THREE.Vector3(0, 1, 0), U);

/** A box in stairs space: centre at `along`/`across` (axisPoint's across)/`y`; size across × height × along. */
function box(along: number, across: number, y: number, sizeAcross: number, height: number, sizeAlong: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(sizeAcross, height, sizeAlong);
  const m = BASIS.clone();
  const [x, z] = axisPoint(along, across);
  m.setPosition(x, y, z);
  return g.applyMatrix4(m);
}

/** A rail between two points in stairs space (along, y) at a given `across`. */
function rail(a0: number, y0: number, a1: number, y1: number, across: number, w: number, d: number): THREE.BufferGeometry {
  const len = Math.hypot(a1 - a0, y1 - y0);
  const g = new THREE.BoxGeometry(w, d, len);
  const tilt = new THREE.Matrix4().makeRotationX(Math.atan2(y0 - y1, a1 - a0)); // local +Z tips down when descending
  const m = BASIS.clone().multiply(tilt);
  const [x, z] = axisPoint((a0 + a1) / 2, across);
  m.setPosition(x, (y0 + y1) / 2, z);
  return g.applyMatrix4(m);
}

export interface StairsParts {
  group: THREE.Group;
  /** Both ends of every tread's side walls (for tests: the body reaches down into the ground everywhere). */
  blocks: Array<{ along: number; topY: number; bottomY: number }>;
  railPosts: Array<{ along: number; across: number; onTerrace: boolean }>;
  lamps: Array<{ along: number; across: number }>;
}

/** Treads down the axis: each flight's steps, the landing folded into the last tread of the flight above it. */
function treads(): Array<{ a0: number; a1: number; y: number }> {
  const out: Array<{ a0: number; a1: number; y: number }> = [];
  STAIRS_PLAN.flights.forEach((f, fi) => {
    for (let i = 0; i < f.steps; i++) out.push({ a0: f.topAlong + i * STEP_TREAD_M, a1: f.topAlong + (i + 1) * STEP_TREAD_M, y: f.topY - (i + 1) * STEP_RISE_M });
    if (fi + 1 < STAIRS_PLAN.flights.length) out[out.length - 1]!.a1 += LANDING_M;
  });
  return out;
}

export function buildStairs(): StairsParts {
  const half = STAIRS_BODY_HALF_M;
  const groundUnder = (along: number): number =>
    Math.min(...[-half, 0, half].map((s) => {
      const [x, z] = axisPoint(along, s);
      return surfaceY(x, z);
    }));

  // One solid body (no overlapping boxes: coplanar overlaps z-fight into a sawtooth): per tread a top, a riser in
  // front of it (facing downhill), and both side walls down into the ground; a last face from the bottom tread down.
  const pos: number[] = [];
  const blocks: StairsParts['blocks'] = [];
  const P = (along: number, across: number, y: number): number[] => {
    const [x, z] = axisPoint(along, across);
    return [x, y, z];
  };
  const quad = (a: number[], b: number[], c: number[], d: number[]): void => {
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
  };
  const steps = treads();
  const bottomAt = (along: number, y: number): number => Math.min(y - 0.24, groundUnder(along) - 0.3);
  let prevY = 0;
  for (const s of steps) {
    const b0 = bottomAt(s.a0, s.y);
    const b1 = bottomAt(s.a1, s.y);
    quad(P(s.a0, -half, s.y), P(s.a0, half, s.y), P(s.a1, half, s.y), P(s.a1, -half, s.y)); // tread
    quad(P(s.a0, -half, prevY), P(s.a0, half, prevY), P(s.a0, half, s.y), P(s.a0, -half, s.y)); // riser
    for (const side of [-half, half]) quad(P(s.a0, side, s.y), P(s.a1, side, s.y), P(s.a1, side, b1), P(s.a0, side, b0));
    blocks.push({ along: s.a0, topY: s.y, bottomY: b0 }, { along: s.a1, topY: s.y, bottomY: b1 });
    prevY = s.y;
  }
  const last = steps[steps.length - 1]!;
  const lastBottom = bottomAt(last.a1, last.y);
  quad(P(last.a1, -half, last.y), P(last.a1, half, last.y), P(last.a1, half, lastBottom), P(last.a1, -half, lastBottom));
  const body = new THREE.BufferGeometry();
  body.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  body.computeVertexNormals();

  // rails: a head post on the terrace, posts every 4 steps and at each flight's end, two rails; across the landing
  const wood: THREE.BufferGeometry[] = [];
  const railPosts: StairsParts['railPosts'] = [];
  const headAlong = STAIRS_TOP_ALONG_M - 0.15;
  const addPost = (along: number, across: number, y: number, onTerrace = false): void => {
    wood.push(box(along, across, y + POST_H / 2, 0.14, POST_H, 0.14));
    railPosts.push({ along, across, onTerrace });
  };
  const flights = STAIRS_PLAN.flights;
  for (const sx of [-1, 1]) {
    const across = sx * STAIR_RAIL_OFFSET_M;
    addPost(headAlong, across, 0, true);
    flights.forEach((f, fi) => {
      const alongAt = (i: number): number => f.topAlong + (i + 0.5) * STEP_TREAD_M;
      const yAt = (i: number): number => f.topY - (i + 1) * STEP_RISE_M;
      for (let i = fi === 0 ? 4 : 0; i < f.steps - 1; i += 4) addPost(alongAt(i), across, yAt(i));
      addPost(alongAt(f.steps - 1), across, yAt(f.steps - 1));
      for (const [h, w, d] of RAIL_HEIGHTS) {
        if (fi === 0) wood.push(rail(headAlong, h, alongAt(0), yAt(0) + h, across, w, d)); // head post -> first step
        wood.push(rail(alongAt(0), yAt(0) + h, alongAt(f.steps - 1), yAt(f.steps - 1) + h, across, w, d));
      }
      const next = flights[fi + 1];
      if (next) {
        for (const [h, w, d] of RAIL_HEIGHTS) {
          wood.push(rail(alongAt(f.steps - 1), yAt(f.steps - 1) + h, next.topAlong + 0.5 * STEP_TREAD_M, next.topY - STEP_RISE_M + h, across, w, d));
        }
      }
    });
  }

  // small lamps outside the left rail, every LAMP_EVERY steps from the 5th
  const lampStone: THREE.BufferGeometry[] = [];
  const lampLight: THREE.BufferGeometry[] = [];
  const lamps: StairsParts['lamps'] = [];
  let k = 0;
  for (const f of flights) {
    for (let i = 0; i < f.steps; i++, k++) {
      if (k % LAMP_EVERY !== LAMP_EVERY - 1 || k <= 4) continue;
      const along = f.topAlong + (i + 0.5) * STEP_TREAD_M;
      const top = f.topY - (i + 1) * STEP_RISE_M;
      lampStone.push(box(along, LAMP_ACROSS, top + 0.45, 0.18, 0.9, 0.18));
      lampLight.push(box(along, LAMP_ACROSS, top + 1.03, 0.26, 0.26, 0.26));
      lamps.push({ along, across: LAMP_ACROSS });
    }
  }

  const group = new THREE.Group();
  group.name = 'stairs';
  const stone = new THREE.Mesh(body, new THREE.MeshStandardMaterial({ color: 0x9a9890, roughness: 0.9, side: THREE.DoubleSide }));
  stone.name = 'stairs_steps';
  stone.userData = { snow: true };
  const rails = new THREE.Mesh(mergeGeometries(wood), new THREE.MeshStandardMaterial({ color: 0x7c614f, roughness: 0.75 }));
  rails.name = 'stairs_rails';
  rails.userData = { snow: true };
  const posts = new THREE.Mesh(mergeGeometries(lampStone), new THREE.MeshStandardMaterial({ color: 0x9a9890, roughness: 0.9 }));
  posts.name = 'stairs_lamp_posts';
  group.add(stone, rails, posts);
  // one mesh per lamp, centred on it: the emissive controller places point lights at mesh positions
  const paper = new THREE.MeshStandardMaterial({ color: 0xf3e6c8, roughness: 0.8 });
  lampLight.forEach((g, i) => {
    g.computeBoundingBox();
    const c = g.boundingBox!.getCenter(new THREE.Vector3());
    g.translate(-c.x, -c.y, -c.z);
    const lamp = new THREE.Mesh(g, paper);
    lamp.position.copy(c);
    lamp.name = `stairs_lamp_${i + 1}`;
    lamp.userData = { emissive: 'lantern' };
    group.add(lamp);
  });
  return { group, blocks, railPosts, lamps };
}
